import React, { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  BoundDatasetsObjectMapping,
  Maybe,
  MdmObject,
  MdmObjectDto,
  useDeleteObjectMutation,
  useGetCoordinatesLazyQuery,
  useGetProjectQuery
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Dropdown } from "primereact/dropdown";
import { getFormErrorMessage } from "../utils/utils";
import AdditionalInfo from "./AdditionalInfo";
import DatasetsTab from "./DatasetsTab";
import ObjectAnalytics from "./ObjectAnalytics";
import ObjectDevices from "./ObjectDevices";
import ObjectJobs from "./ObjectJobs";
import ObjectNotes from "./ObjectNotes";

type ObjectFormProps = {
  object: MdmObjectDto | null;
  onSubmit: (object: MdmObjectDto) => void;
};

const emptyObject: Partial<MdmObjectDto> = {
  tags: [],
};

const schema = z.object({
  title: z.string().min(1, "Required"),
});

const ObjectForm: React.FC<ObjectFormProps> = ({ object, onSubmit }) => {
  const [editable, setEditable] = useState(!object);
  const [objectState, setObjectState] = useState<MdmObjectDto>(object ?? emptyObject as MdmObjectDto);
  const navigate = useNavigate();
  const { projectId } = useParams();

  const { data: projectData } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });
  const objectAttributes = projectData?.mdmProjectById?.configuration?.objectConfiguration.customFields ?? [];

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const [deleteObjectMutation] = useDeleteObjectMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Object deleted successfully",
        life: 3000,
      });
      navigate(`/mdm/${projectId}/`);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
  });

  const [getCoordinates, { data, loading, error }] = useGetCoordinatesLazyQuery();

  useEffect(() => {
    if (data && data.coordinates) {
      setObjectState(prev => ({
        ...prev,
        location: {
          ...prev.location,
          latitude: data.coordinates[0]?.latitude,
          longitude: data.coordinates[0]?.longitude,
        },
      }));
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, address: { ...prev.address, [name]: value } } as MdmObjectDto));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this object?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (object) {
          deleteObjectMutation({
            variables: { id: object.id! },
            update(cache) {
              const normalizedId = cache.identify({ id: object.id, __typename: "MdmObjectDTO" });
              cache.evict({ id: normalizedId });
              cache.gc();
            },
          });
        }
      },
    });
  };

  const renderTitleControl = (field: ControllerRenderProps<Partial<MdmObject>, any>) => (
    <InputLabelWrapper labelName="Object Name" col={4}>
      <InputText
        value={field.value}
        onChange={e => {
          field.onChange(e.target.value);
          handleChange(e);
        }}
        className="p-inputtext-sm"
        name="title"
        placeholder="Object Name"
        disabled={!editable}
      />
      {getFormErrorMessage("title", errors)}
    </InputLabelWrapper>
  );
  return (
    <div className="flex justify-content-between flex-column gap-5 mx-auto">
      <ConfirmPopup />
      <form>
        <div className="grid p-fluid">
          <InputLabelWrapper labelName="Identifier" col={2}>
            <InputText
              value={objectState.identifier ?? ""}
              onChange={handleChange}
              className="p-inputtext-sm"
              name="identifier"
              placeholder="Identifier"
              disabled={!!object}
            />
          </InputLabelWrapper>
          <Controller
            defaultValue={object?.title ?? ""}
            control={control}
            name="title"
            render={({ field }) => renderTitleControl(field)}
          />
          <InputLabelWrapper labelName="Description" col={4}>
            <InputText
              value={objectState.description ?? ""}
              onChange={handleChange}
              className="p-inputtext-sm"
              name="description"
              placeholder="Description"
              disabled={!editable}
            />
          </InputLabelWrapper>
          <div className="text-right col-2 mt-2 ">
            {object?.location && (
              <Button
                type="button"
                icon="pi pi-map"
                title="Show on map"
                className="p-button-rounded p-button-text"
                onClick={() => navigate(`/mdm/${projectId}/map/${object?.id}`)}
              />
            )}
            {!editable && (
              <Button
                type="button"
                icon="pi pi-pencil"
                title="Edit"
                className="p-button-rounded p-button-text"
                onClick={() => setEditable(true)}
              />
            )}
            {editable && object && (
              <Button
                type="button"
                icon="pi pi-trash"
                title="Delete"
                className="p-button-rounded p-button-text p-button-danger"
                onClick={handleDelete}
              />
            )}
          </div>
          <InputLabelWrapper labelName="Country" col={2}>
            <InputText
              value={objectState.address?.country ?? ""}
              onChange={handleAddressChange}
              className="p-inputtext-sm"
              name="country"
              placeholder="Country"
              disabled={!editable}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="City" col={3}>
            <InputText
              value={objectState.address?.city ?? ""}
              onChange={handleAddressChange}
              className="p-inputtext-sm"
              name="city"
              placeholder="City"
              disabled={!editable}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Street" col={3}>
            <InputText
              value={objectState.address?.street ?? ""}
              onChange={handleAddressChange}
              className="p-inputtext-sm"
              name="street"
              placeholder="Street"
              disabled={!editable}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="House number" col={2}>
            <InputText
              value={objectState.address?.houseNumber ?? ""}
              onChange={handleAddressChange}
              className="p-inputtext-sm"
              name="houseNumber"
              placeholder="House number"
              disabled={!editable}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Post code" col={2}>
            <InputText
              value={objectState.address?.postCode ?? ""}
              onChange={handleAddressChange}
              className="p-inputtext-sm"
              name="postCode"
              placeholder="Post code"
              disabled={!editable}
            />
          </InputLabelWrapper>
          <InputLabelWrapper labelName="Latitude/Longitude">
            <div className="p-inputgroup">
              <Button
                type="button"
                loading={loading}
                label="Get coordinates"
                onClick={() => {
                  const { fullAddress, ...address } = objectState.address!;
                  getCoordinates({
                    variables: { address },
                  });
                }}
                disabled={!editable}
              />
              <InputNumber
                value={objectState.location?.latitude}
                onChange={e =>
                  setObjectState(prev => ({
                    ...prev,
                    location: { ...prev.location, latitude: e.value },
                  }))
                }
                className="p-inputtext-sm"
                name="latitude"
                placeholder="Latitude"
                useGrouping={false}
                maxFractionDigits={10}
                disabled={!editable}
              />
              <InputNumber
                value={objectState.location?.longitude}
                onChange={e =>
                  setObjectState(prev => ({
                    ...prev,
                    location: { ...prev.location, longitude: e.value },
                  }))
                }
                className="p-inputtext-sm"
                name="longitude"
                placeholder="Longitude"
                useGrouping={false}
                maxFractionDigits={10}
                disabled={!editable}
              />
            </div>
          </InputLabelWrapper>
          {error && <div className="flex align-items-end col-6 text-red-500">{error.message}</div>}
          <InputLabelWrapper labelName="Tags">
            <Chips
              value={objectState.tags ?? []}
              onChange={handleChange}
              name="tags"
              placeholder="Tags.."
              disabled={!editable}
            />
          </InputLabelWrapper>

          {objectAttributes.length > 0 && (
            <AdditionalInfo
              attributes={objectAttributes}
              objectState={objectState}
              setObjectState={setObjectState}
              editable={editable}
            />
          )}
        </div>
      </form>

      {object && (
        <TabView className="col-12">
          <TabPanel header="Devices">
            <ObjectDevices object={object} />
          </TabPanel>
          <TabPanel header="Datasets">
            <ObjectDatasets object={object} />
          </TabPanel>
          <TabPanel header="Analytics">
            <ObjectAnalytics object={object} tableName="AnalyticsByObject"/>
          </TabPanel>
          <TabPanel header="Notes">
            <ObjectNotes object={object} />
          </TabPanel>
          <TabPanel header="Jobs">
            <ObjectJobs object={object} tableName="JobsByObject"/>
          </TabPanel>
        </TabView>
      )}

      <div className="flex w-full justify-content-end gap-3">
        {editable && (
          <Button
            type="submit"
            onClick={handleSubmit(() => onSubmit(objectState))}
            label={object ? "Update" : "Create"}
            className={`p-button-rounded p-button-${object ? "primary" : "success"} w-10rem`}
          />
        )}
        {object && editable && (
          <Button
            type="button"
            onClick={() => {
              setEditable(false);
              setObjectState(object ?? emptyObject);
            }}
            label="Cancel"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
        {(!object || !editable) && (
          <Button
            type="button"
            onClick={() => navigate(-1)}
            label="Back"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
      </div>
    </div>
  );
};

export default ObjectForm;

const ObjectDatasets: React.FC<{ object: MdmObjectDto }> = ({object}) => {
  const options = [
    { title: "All Datasets", data: object.boundDatasets.all },
    { title: "Object Datasets", data: object.boundDatasets.direct },
    { title: "Devices Datasets", data: object.boundDatasets.indirect },
  ];

  const [datasets, setDatasets] = useState<{
    title: string;
    data: Maybe<BoundDatasetsObjectMapping>[];
  }>(options[0]);

  return (
    <div className="flex flex-column align-items-start">
      <Dropdown
        value={datasets}
        options={options}
        optionLabel="title"
        onChange={e => setDatasets(e.value)}
        className="ml-3"
      />
      <DatasetsTab datasets={datasets.data?.map(d => d?.dataset!)} />
    </div>
  );
};
